import { Check, ImageIcon, SkipForward, Sparkles } from "lucide-react";
import { OptionCard } from "./OptionCard";

const Chip = ({ selected, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
    className={`h-11 rounded-full border px-4 text-sm font-semibold transition-[border-color,background-color,color,transform] duration-200 active:scale-95 ${
      selected ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
    {children}
  </button>
);

const Group = ({ title, children }) => (
  <div>
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    <div className="mt-2.5 flex flex-wrap gap-2">{children}</div>
  </div>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const PreviewStep = ({ value, onChange, photos, styles, prefs }) => {
  const set = (patch) => onChange((v) => ({ ...v, ...patch }));
  const choose = (want) => onChange((v) => ({ ...v, want, photo_path: v.photo_path || photos[0]?.path || null }));
  const setPref = (label, option) => onChange((v) => {
    const next = { ...v.preferences };
    if (next[label] === option) delete next[label]; else next[label] = option;
    return { ...v, preferences: next };
  });
  return (
    <div className="space-y-6" data-testid="preview-step">
      <div className="grid gap-3">
        <OptionCard icon={Sparkles} title="Yes, show me" hint="Free AI preview based on your photo" selected={value.want === true}
          onClick={() => choose(true)} testId="preview-opt-in" compact />
        <OptionCard icon={SkipForward} title="No thanks, skip" selected={value.want === false} onClick={() => choose(false)} testId="preview-skip" compact />
      </div>
      {value.want && (
        <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="preview-options">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><ImageIcon className="h-4 w-4" />Pick your main photo</p>
            <div className="mt-2.5 grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <button key={p.id} type="button" onClick={() => set({ photo_path: p.path })} data-testid={`preview-photo-${i}`} aria-pressed={value.photo_path === p.path}
                  className={`relative aspect-square overflow-hidden rounded-xl ring-2 transition-[box-shadow] ${value.photo_path === p.path ? "ring-[color:var(--brand)]" : "ring-transparent"}`}>
                  <img src={p.preview} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                  {value.photo_path === p.path && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[color:var(--brand)] text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>}
                </button>
              ))}
            </div>
          </div>
          <Group title="Style">
            {styles.map((s) => <Chip key={s} selected={value.style === s} onClick={() => set({ style: s })} testId={`preview-style-${slug(s)}`}>{s}</Chip>)}
          </Group>
          {prefs.map(({ label, options }) => (
            <Group key={label} title={`${label} (optional)`}>
              {options.map((o) => (
                <Chip key={o} selected={value.preferences[label] === o} onClick={() => setPref(label, o)} testId={`preview-pref-${slug(label)}-${slug(o)}`}>{o}</Chip>
              ))}
            </Group>
          ))}
          <p className="text-xs text-slate-500">AI-generated illustration for inspiration only. Not a guarantee of final results.</p>
        </div>
      )}
    </div>
  );
};
